// Helpers for the template testing workflow. The org's designated test
// patient is an ordinary patient row whose id is stored at
// organization.settings.test_patient_id (no patient schema flag). Notes for
// that patient resolve a template's unpublished draft (draft_sections /
// draft_variables) so admins can exercise changes across a whole case before
// publishing. See docs/XANO_CHANGELOG.md 2026-09-08 and the note-templates
// test-run page.

/**
 * The org's designated test patient id, or null when none has been set up.
 * @param {Object} user - Current user (with organization.settings loaded)
 * @returns {string|null}
 */
export function getTestPatientId(user) {
  return user?.organization?.settings?.test_patient_id ?? null;
}

/**
 * Whether the given patient is the org's designated test patient.
 * @param {string} patientId
 * @param {Object} user
 * @returns {boolean}
 */
export function isTestPatient(patientId, user) {
  if (!patientId) return false;
  const testId = getTestPatientId(user);
  return !!testId && testId === patientId;
}

/**
 * Resolve the template body to use for a note. For the test patient, a
 * template with unpublished changes resolves to its draft so test notes
 * exercise the draft; everyone else always gets the live body.
 *
 * v1 caveat: plan-of-care generation, PDF export, and superbill rendering
 * resolve the live template. Field ids are shared between draft and live, so
 * this only matters for brand-new draft-only fields, which those paths won't
 * see until publish.
 *
 * @param {Object} template - Full template record (with sections)
 * @param {boolean} isTest - Whether the note belongs to the test patient
 * @returns {Object} Template with sections/variables swapped to the draft when applicable
 */
export function resolveTemplateDraft(template, isTest) {
  if (!isTest || !template) return template;
  if (!Array.isArray(template.draft_sections) || template.draft_sections.length === 0) return template;
  return {
    ...template,
    sections: template.draft_sections,
    variables: template.draft_variables ?? template.variables,
  };
}
